/**
 * Geometry for the profile-photo crop. The photo is shown "cover"-fitted
 * inside a square viewport (the visible circle is that square, rounded), then
 * zoomed and dragged. `x`/`y` are how far the photo's centre sits from the
 * viewport's centre, in viewport pixels.
 */

export const AVATAR_VIEW_PX = 260;
export const AVATAR_OUTPUT_PX = 512;
export const AVATAR_MAX_ZOOM = 4;
const AVATAR_JPEG_QUALITY = 0.88;

export interface CropState {
  zoom: number;
  x: number;
  y: number;
}

/** Scale at which the photo exactly covers the viewport (zoom = 1). */
export function coverScale(width: number, height: number, view = AVATAR_VIEW_PX): number {
  return Math.max(view / width, view / height);
}

/** Keeps the photo covering the viewport: no empty edge can show inside the circle. */
export function clampCrop(width: number, height: number, crop: CropState, view = AVATAR_VIEW_PX): CropState {
  const zoom = Math.min(AVATAR_MAX_ZOOM, Math.max(1, crop.zoom));
  const scale = coverScale(width, height, view) * zoom;
  const maxX = Math.max(0, (width * scale - view) / 2);
  const maxY = Math.max(0, (height * scale - view) / 2);
  return {
    zoom,
    x: Math.min(maxX, Math.max(-maxX, crop.x)),
    y: Math.min(maxY, Math.max(-maxY, crop.y)),
  };
}

/** The square of the original photo (in its own pixels) that the viewport shows. */
export function sourceRect(width: number, height: number, crop: CropState, view = AVATAR_VIEW_PX) {
  const scale = coverScale(width, height, view) * crop.zoom;
  const size = view / scale;
  return {
    sx: width / 2 - crop.x / scale - size / 2,
    sy: height / 2 - crop.y / scale - size / 2,
    size,
  };
}

/** Renders the chosen square to a JPEG. Null if the browser can't encode it. */
export async function renderAvatar(img: HTMLImageElement, crop: CropState): Promise<Blob | null> {
  const width = img.naturalWidth;
  const height = img.naturalHeight;
  const safe = clampCrop(width, height, crop);
  const { sx, sy, size } = sourceRect(width, height, safe);
  const canvas = document.createElement('canvas');
  canvas.width = AVATAR_OUTPUT_PX;
  canvas.height = AVATAR_OUTPUT_PX;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.fillStyle = '#fff'; // JPEG has no transparency: PNG transparency becomes white, not black
  ctx.fillRect(0, 0, AVATAR_OUTPUT_PX, AVATAR_OUTPUT_PX);
  ctx.drawImage(img, sx, sy, size, size, 0, 0, AVATAR_OUTPUT_PX, AVATAR_OUTPUT_PX);
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', AVATAR_JPEG_QUALITY));
}
